import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { servicesApi, pagesApi } from "../services/api";
import HeroSection from "../components/HeroSection";
import { RichContent } from "../components/RichContent";

function CorporateTrainingPage() {
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sectionHeadings, setSectionHeadings] = useState(null);

  useEffect(() => {
    fetchCorporateTrainingData();
  }, []);

  const fetchCorporateTrainingData = async () => {
    setLoading(true);
    try {
      const [programsRes, benefitsRes, processRes, pageDataRes, sectionsRes] =
        await Promise.all([
          servicesApi.getCorporateTrainingPrograms(),
          servicesApi.getCorporateTrainingBenefits(),
          servicesApi.getCorporateTrainingProcess(),
          pagesApi.getByPageType("corporate-training"),
          servicesApi.getCorporateTrainingSections(),
        ]);

      const programs = programsRes.data || [];
      setTrainingPrograms(
        programs.map((prog) => ({
          ...prog,
          topics: prog.topics ? prog.topics.split(",") : [],
        })),
      );
      setPageData(pageDataRes.data || []);
      setBenefits(benefitsRes.data || []);
      setProcessSteps(processRes.data || []);
      setSectionHeadings(sectionsRes.data || null);
    } catch (error) {
      console.error("Error loading corporate training data:", error);
      setFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const setFallbackData = () => {
    setTrainingPrograms([
      {
        title: "Leadership Development",
        description:
          "Build effective leaders at all levels of your organization",
        topics: [
          "Strategic Leadership",
          "Team Management",
          "Decision Making",
          "Change Leadership",
        ],
        duration: "2-3 days",
        icon: "👑",
      },
      {
        title: "Digital Transformation",
        description: "Equip your team with skills for the digital age",
        topics: [
          "Digital Strategy",
          "Agile Methodologies",
          "Data Literacy",
          "Innovation Culture",
        ],
        duration: "3-5 days",
        icon: "💻",
      },
      {
        title: "Communication Excellence",
        description: "Enhance interpersonal and organizational communication",
        topics: [
          "Effective Presentations",
          "Influential Communication",
          "Conflict Resolution",
          "Cross-cultural Communication",
        ],
        duration: "2 days",
        icon: "🗣️",
      },
      {
        title: "Strategic Thinking",
        description: "Develop strategic mindset and planning capabilities",
        topics: [
          "Business Acumen",
          "Strategic Analysis",
          "Scenario Planning",
          "Execution Strategy",
        ],
        duration: "2-3 days",
        icon: "🎯",
      },
    ]);

    useEffect(() => {
      if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
        const interval = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
        }, pageData.slider_interval || 5000);
        return () => clearInterval(interval);
      }
    }, [pageData]);

    setBenefits([
      {
        title: "Tailored Content",
        description:
          "Customized training aligned with your organizational goals",
        icon: "🎯",
      },
      {
        title: "Practical Approach",
        description: "Real-world case studies and hands-on exercises",
        icon: "🛠️",
      },
      {
        title: "Measurable ROI",
        description: "Track progress and measure training effectiveness",
        icon: "📊",
      },
      {
        title: "Expert Facilitators",
        description: "Industry experts with practical experience",
        icon: "👨‍🏫",
      },
    ]);

    setProcessSteps([
      {
        step_number: 1,
        title: "Needs Assessment",
        description: "Understand your goals and challenges",
      },
      {
        step_number: 2,
        title: "Program Design",
        description: "Create customized training content",
      },
      {
        step_number: 3,
        title: "Delivery",
        description: "Engaging and interactive sessions",
      },
      {
        step_number: 4,
        title: "Evaluation",
        description: "Measure impact and ROI",
      },
    ]);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">
              {sectionHeadings?.benefits_title ||
                "Why Choose Innobig for Corporate Training"}
            </h2>
            {sectionHeadings?.benefits_subtitle && (
              <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
                {sectionHeadings.benefits_subtitle}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">
              {sectionHeadings?.programs_title || "Sample Training Programs"}
            </h2>
            {sectionHeadings?.programs_subtitle && (
              <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
                {sectionHeadings.programs_subtitle}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {trainingPrograms.map((program, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex items-start mb-6">
                    <div className="text-4xl mr-4">{program.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">
                        {program.title}
                      </h3>
                      <p className="text-gray-600">
                        <RichContent html={program.description} />
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Key Topics Covered:</h4>
                    <div className="flex flex-wrap gap-2">
                      {program.topics.map((topic, idx) => (
                        <span
                          key={idx}
                          className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{program.duration}</span>
                    <Link
                      to={`/corporate-training/program/${program.id}`}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Training Development Process
          </h2>
          <div className="bg-white rounded-xl shadow p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                    {step.step_number}
                  </div>
                  <h3 className="font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Transform Your Organization
          </h2>
          <p className="mb-8 text-blue-100">
            Invest in your team's development and drive organizational
            excellence.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Request a Custom Training Proposal
          </Link>
        </div>
      </section>
    </div>
  );
}

export default CorporateTrainingPage;
